import { useCallback, useState } from 'react';
import { useT } from '../translation/src';
import { useOrganisationInvoicePdfLazyQuery } from '../types/graphqlTypes';
import { ToastType, useToastNotifications } from './useToastNotifications';

// Saves a PDF that came as base64 under its file name, through a link that is clicked and dropped again.
export const savePdf = ({ filename, base64 }: { filename: string; base64: string }) => {
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// Downloads the invoice sales sent for a purchase-history row (a row with a sentInvoiceNumber).
// downloadingEntryId is the row being fetched, for a busy state on its link.
export const useDownloadInvoice = ({ organisationId }: { organisationId: string }) => {
  // Not cached: a PDF is only in the way there, and the next click should get what Odoo has then.
  const [getInvoicePdf] = useOrganisationInvoicePdfLazyQuery({ fetchPolicy: 'no-cache' });
  const [downloadingEntryId, setDownloadingEntryId] = useState<string | null>(null);
  const { showToast } = useToastNotifications();
  const failed = useT('could not download the invoice', { swc: true });

  const downloadInvoice = useCallback(
    async (entryId: string) => {
      setDownloadingEntryId(entryId);
      try {
        const { data, error } = await getInvoicePdf({ variables: { organisationId, entryId } });
        if (!data) throw error ?? new Error('NO_DATA');
        savePdf(data.organisationInvoicePdf);
      } catch (e) {
        showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      } finally {
        setDownloadingEntryId(null);
      }
    },
    [failed, getInvoicePdf, organisationId, showToast],
  );

  return { downloadInvoice, downloadingEntryId };
};
